import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { redirectToApp } from "../utils/redirectToApp";

const MEETING_LEFT_KEY = "meeting_left_or_ended";

export default function MeetingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const meetingLeftRef = useRef(false);

  const meetingId = searchParams.get("meetingId");
  const userId = searchParams.get("userId");
  const role = (searchParams.get("role") || "")
    .split("?")[0]
    .split("&")[0]
    .trim();

  const [meetingLink, setMeetingLink] = useState<string | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [loadingLabelEn, setLoadingLabelEn] = useState(true);

  const parseStartTime = useCallback((value: unknown): Date | null => {
    if (value == null || typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const iso = trimmed.replace(" ", "T");
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const parseEndTime = useCallback(
    (value: unknown): Date | null => {
      return parseStartTime(value);
    },
    [parseStartTime],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingLabelEn((prev) => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadingText = loadingLabelEn
    ? "Loading meeting"
    : "جاري تحميل الاجتماع";

  const goToHome = useCallback(() => {
    sessionStorage.setItem(MEETING_LEFT_KEY, "true");
    const query = role ? `?role=${encodeURIComponent(role)}` : "";
    navigate(`/${query}`, { replace: true });
  }, [navigate, role]);

  useEffect(() => {
    if (!meetingId) {
      setError("Meeting not found");
      setLoading(false);
      return;
    }

    // Allow rejoining: clear "left meeting" flag when user opens the meeting link again
    sessionStorage.removeItem(MEETING_LEFT_KEY);

    let cancelled = false;
    const url = `${API_BASE_URL}/sessions/join-info`;
    const params = { userId, role, meetingId };

    axios
      .get(url, { params: Object.keys(params).length ? params : undefined })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        if (
          data?.success === false &&
          (data?.statusCode === 404 || data?.error === "NotFoundException")
        ) {
          setError(
            "This meeting link is invalid or has expired. Please use the correct link.",
          );
          return;
        }
        const link = data?.link ?? data?.data?.link;
        const startTimeRaw = data?.startTime ?? data?.data?.startTime;
        const endTimeRaw = data?.endTime ?? data?.data?.endTime;
        if (link && typeof link === "string") {
          const endTime = parseEndTime(endTimeRaw);
          if (endTime != null && endTime.getTime() <= Date.now()) {
            setError("Session has ended.");
            return;
          }
          setMeetingLink(link);
          setSessionStartTime(parseStartTime(startTimeRaw) ?? null);
          setIframeLoaded(false);
          sessionStorage.removeItem(MEETING_LEFT_KEY);
        } else {
          setError("Invalid meeting response");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (
            err.response?.status === 404 ||
            err.response?.data?.statusCode === 404
          ) {
            setError(
              "This meeting link is invalid or has expired. Please use the correct link.",
            );
          } else {
            const msg =
              err.response?.data?.message ??
              err.message ??
              "Something went wrong. Please try again.";
            setError(msg);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [meetingId, userId, role, goToHome, parseStartTime, parseEndTime]);

  useEffect(() => {
    if (!meetingLink) return;
    const handler = (event: MessageEvent) => {
      const d = event?.data;
      if (process.env.NODE_ENV === "development") {
        console.debug("[Meeting iframe message]", event.origin, d);
      }
      if (!d || typeof d !== "object") return;
      if (
        d.type === "dyte-room-left" ||
        d.type === "rtk-room-left" ||
        d.type === "realtimekit-room-left" ||
        d.type === "roomLeft" ||
        d.event === "meetingEnded" ||
        d.event === "meeting-ended" ||
        d.roomLeft === true
      ) {
        goToHome();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [meetingLink, goToHome]);

  // At startTime + 11 minutes, verify session from response body (API may return 200 with success: false)
  useEffect(() => {
    if (!meetingLink || !meetingId) return;

    const start = sessionStartTime;
    if (!start) {
      return;
    }

    const SESSION_CHECK_OFFSET_MS = 11 * 60 * 1000; // 11 minutes after startTime
    const checkAt = new Date(start.getTime() + SESSION_CHECK_OFFSET_MS);
    const now = Date.now();
    if (checkAt.getTime() <= now) {
      return;
    }

    const delayMs = checkAt.getTime() - now;
    const url = `${API_BASE_URL}/sessions/join-info`;
    const params = { userId, role, meetingId };

    const timeoutId = window.setTimeout(() => {
      axios
        .get(url, { params: Object.keys(params).length ? params : undefined })
        .then((res) => {
          const data = res?.data;
          const success = data?.success;
          const statusCode = data?.statusCode;

          // Only end session when API explicitly indicates failure: success === false, or statusCode present and not 200
          const isSessionEnded =
            success === false || (statusCode != null && statusCode !== 200);
          if (isSessionEnded) {
            setError("Session has ended.");
          }
        })
        .catch((err) => {
          console.log("[Session check] API failed:", err?.message ?? err);
          setError("Session has ended.");
        });
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [meetingLink, meetingId, userId, role, sessionStartTime]);

  useEffect(() => {
    const handlePopState = () => {
      if (
        meetingLeftRef.current ||
        sessionStorage.getItem(MEETING_LEFT_KEY) === "true"
      ) {
        window.history.pushState(null, "", window.location.href);
        goToHome();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [goToHome]);

  useEffect(() => {
    const isKnownMediaError = (msg: string) =>
      msg.includes("No audio output devices") ||
      msg.includes("ERR1608") ||
      msg.includes("ERR1100") ||
      msg.includes("Unsupported concurrent calls") ||
      msg.includes("No speaker found") ||
      msg.includes("LocalMediaHandler") ||
      msg.includes("AwaitQueue stopped") ||
      msg.includes("Socket is not connected");

    const onError = (event: ErrorEvent) => {
      const msg = typeof event.message === "string" ? event.message : "";
      if (isKnownMediaError(msg)) {
        event.preventDefault();
        event.stopPropagation();
        console.warn(
          "iOS/media error suppressed (e.g. after background):",
          msg,
        );
        return true;
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const msg =
        typeof event.reason === "string"
          ? event.reason
          : (event.reason?.message ?? String(event.reason ?? ""));
      if (isKnownMediaError(msg)) {
        event.preventDefault();
        console.warn("iOS/media promise rejection suppressed:", msg);
      }
    };

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black px-4">
        <div className="[&_.ant-spin-dot-item]:!bg-white">
          <Spin size="large" />
        </div>
        <p className="text-gray-400 mt-4">{loadingText}</p>
      </div>
    );
  }

  if (error) {
    const isInvalidLinkMessage =
      error ===
      "This meeting link is invalid or has expired. Please use the correct link.";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black px-6">
        <div className="text-center max-w-md">
          <img
            src="/assets/icons/IthnainLogoLight.svg"
            alt="Ithnain Logo"
            className="mx-auto mb-6 h-14 w-auto opacity-90"
          />
          <div className="rounded-xl bg-white/5 border border-white/10 px-6 py-6 mb-6">
            <p className="text-lg text-white font-medium mb-2">{error}</p>
            {isInvalidLinkMessage && (
              <p
                className="text-gray-400 text-base leading-relaxed"
                dir="rtl"
                lang="ar"
              >
                رابط الاجتماع غير صالح أو منتهي الصلاحية. يرجى استخدام الرابط
                الصحيح.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={goToHome}
            className="px-6 py-3 rounded-lg bg-[#042C43] hover:bg-[#053a52] text-white font-medium inline-flex items-center gap-2"
          >
            <HomeOutlined />
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (meetingLink) {
    return (
      <div
        className="fixed inset-0 flex flex-col bg-gray-900 overflow-hidden meeting-viewport"
        style={{
          width: "100%",
          minHeight: 0,
        }}
      >
        <div className="flex-none flex justify-between items-center px-3 py-2 bg-gray-900 shrink-0">
          <div
            role="button"
            tabIndex={0}
            onClick={() => redirectToApp(role)}
            onKeyDown={(e) => e.key === "Enter" && redirectToApp(role)}
            className="cursor-pointer focus:outline-none"
            title="Back to app"
          >
            <img
              src="/assets/icons/IthnainLogoLight.svg"
              alt="Ithnain"
              className="h-8 w-auto"
            />
          </div>
          <button
            type="button"
            onClick={goToHome}
            className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
            title="Leave meeting and go home"
          >
            <HomeOutlined className="text-xl" />
          </button>
        </div>
        <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden">
          {!iframeLoaded && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black"
              aria-hidden="true"
            >
              <div className="[&_.ant-spin-dot-item]:!bg-white">
                <Spin size="large" />
              </div>
              <p className="text-gray-400 mt-4">{loadingText}</p>
            </div>
          )}
          <iframe
            title="Meeting"
            src={meetingLink}
            allow="camera *; microphone *; fullscreen *"
            className="absolute inset-0 w-full h-full border-0 block max-w-full max-h-full"
            style={{ width: "100%", height: "100%" }}
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      </div>
    );
  }

  return null;
}
